import { ISlicedPolygons } from '../../types/API/Region';
import {
  IBiomass,
  IBiomassRequest,
  IDeforestationResponse,
  IHeatmapZipLinkResponse,
  ISplitPolygon,
  ISplitPolygonState,
} from '../../types/Geo';
import { formatCarbonData, formatForestData } from '../../utils/biomass';
import { setSplitDrawnPolygon } from '../features/draw/draw-slice';
import {
  setCarbonAccounting,
  setCarbonAccountingChartData,
  setCarbonLoading,
  setDeforestation,
  setDeforestationLoading,
  setForestCover,
  setForestCoverChartData,
  setForestCoverLoading,
} from '../features/region/region-slice';
import { RootState } from '../store';
import { baseApi } from './index';

export const biomassApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCarbonAccountingData: builder.mutation<
      IBiomass | {},
      IBiomassRequest & {
        returnOnly?: boolean;
      }
    >({
      query: (body) => ({
        url: '/biomass/carbon-accounting',
        method: 'POST',
        body,
      }),
      async onQueryStarted(
        { returnOnly = false },
        { queryFulfilled, dispatch, getState }
      ) {
        dispatch(setCarbonLoading(true));
        const { data } = await queryFulfilled;

        if ('data' in data) {
          dispatch(setCarbonLoading(false));

          if (!returnOnly) {
            dispatch(setCarbonAccounting(data));
            const chartData = formatCarbonData(
              data,
              'data',
              (getState() as RootState).userState.user
            );
            dispatch(setCarbonAccountingChartData(chartData));
          }
        }
      },
    }),
    getCarbonAccountingByArea: builder.mutation<
      IBiomass | {},
      IBiomassRequest & {
        returnOnly?: boolean;
      }
    >({
      query: (body) => ({
        url: '/biomass/carbon-accounting-by-area',
        method: 'POST',
        body,
      }),
      async onQueryStarted(
        { returnOnly = false },
        { queryFulfilled, dispatch, getState }
      ) {
        dispatch(setCarbonLoading(true));
        const { data } = await queryFulfilled;

        if ('data' in data) {
          dispatch(setCarbonLoading(false));

          if (!returnOnly) {
            dispatch(setCarbonAccounting(data));
            const chartData = formatCarbonData(
              data,
              'data/area',
              (getState() as RootState).userState.user
            );
            dispatch(setCarbonAccountingChartData(chartData));
          }
        }
      },
    }),
    getForestCoverData: builder.mutation<
      IBiomass | {},
      IBiomassRequest & {
        returnOnly?: boolean;
      }
    >({
      query: (body) => ({
        url: '/biomass/non-forest-cover',
        method: 'POST',
        body,
      }),
      async onQueryStarted(
        { returnOnly = false },
        { queryFulfilled, dispatch, getState }
      ) {
        dispatch(setForestCoverLoading(true));
        const { data } = await queryFulfilled;

        if ('data' in data) {
          dispatch(setForestCoverLoading(false));

          if (!returnOnly) {
            dispatch(setForestCover(data));
            const chartData = formatForestData(
              data,
              (getState() as RootState).regionState.forestTemporalType,
              (getState() as RootState).regionState.forestDataType,
              (getState() as RootState).regionState.calculatedArea
            );
            dispatch(setForestCoverChartData(chartData));
          }
        }
      },
    }),
    getDeforestationData: builder.mutation<
      IDeforestationResponse,
      Omit<IBiomassRequest, 'type'>
    >({
      query: (body) => ({
        url: '/biomass/deforestation-pixels',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        dispatch(setDeforestationLoading(true));
        const { data } = await queryFulfilled;

        if ('data' in data && data.data.length > 0) {
          dispatch(setDeforestationLoading(false));
          dispatch(setDeforestation(data));
        }
      },
    }),
    getHeatmapsZipLink: builder.mutation<
      IHeatmapZipLinkResponse,
      GeoJSON.FeatureCollection<GeoJSON.Polygon>
    >({
      query: (body) => ({
        url: '/biomass/heatmaps-zip-link',
        method: 'POST',
        body,
      }),
    }),
    getSplitPolygonData: builder.mutation<ISplitPolygonState, ISplitPolygon>({
      query: (body) => ({
        url: '/biomass/split-polygon',
        method: 'POST',
        params: {
          omitCache: body.omitCache,
        },
        body: {
          type: 'FeatureCollection',
          features: [body.feature],
        },
      }),
      transformResponse: (response: ISlicedPolygons['byType']) => {
        const drawnDataOnTile = response.withData;
        const drawnDataOutsideTile = response.withoutData;

        const emptyFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Polygon> =
          {
            type: 'FeatureCollection',
            features: [],
          };

        return {
          drawnDataOnTile: drawnDataOnTile || emptyFeatureCollection,
          drawnDataOutsideTile: drawnDataOutsideTile || emptyFeatureCollection,
        };
      },
      async onQueryStarted(
        { withStateSet = true },
        { dispatch, queryFulfilled }
      ) {
        dispatch(setSplitDrawnPolygon(undefined));
        const { data } = await queryFulfilled;
        if (withStateSet) {
          dispatch(setSplitDrawnPolygon(data));
        }
      },
    }),
    recacheTile: builder.mutation<undefined, { tileName: string }>({
      query: ({ tileName }) => {
        return {
          url: `/biomass/recache/${tileName}`,
          method: 'POST',
        };
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetForestCoverDataMutation,
  useGetCarbonAccountingDataMutation,
  useGetCarbonAccountingByAreaMutation,
  useGetSplitPolygonDataMutation,
  useGetDeforestationDataMutation,
  useGetHeatmapsZipLinkMutation,
  useRecacheTileMutation,
} = biomassApi;
